import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Shield, CheckCircle, WarningOctagon, XCircle, MapPinLine, ClockClockwise, Crosshair } from "@phosphor-icons/react";
import { format } from "date-fns";

const Choice = ({ value, label, color, icon: Icon, onClick, disabled, testid }) => (
  <button
    data-testid={testid}
    disabled={disabled}
    onClick={() => onClick(value)}
    className={`flex flex-col items-center justify-center gap-2 p-5 border ${color} hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
  >
    <Icon size={26} weight="duotone" />
    <span className="text-sm font-semibold uppercase tracking-wider">{label}</span>
  </button>
);

const getPosition = () => new Promise((resolve) => {
  if (!navigator.geolocation) { resolve(null); return; }
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy_m: pos.coords.accuracy }),
    () => resolve(null),
    { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
  );
});

const CheckIn = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responded, setResponded] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifyState, setVerifyState] = useState(null); // { verified, distance_m }
  const [params] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/checkin/${token}`);
      setInfo(data);
      if (data.response_value) {
        setResponded(true);
        setVerifyState({ verified: data.arrival_verified, distance_m: data.arrival_distance_m });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid link");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { const id = setTimeout(load, 0); return () => clearTimeout(id); }, [load]);

  const respond = useCallback(async (value) => {
    setBusy(true);
    let payload = { response: value };
    // Always try to capture GPS for any response; useful to log where guards are when they can't make it
    const pos = await getPosition();
    if (pos) payload = { ...payload, ...pos };
    try {
      const { data } = await api.post(`/checkin/${token}/respond`, payload);
      setVerifyState({ verified: data.arrival_verified, distance_m: data.arrival_distance_m });
      setResponded(true);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to record response");
    } finally { setBusy(false); }
  }, [token, load]);

  useEffect(() => {
    const r = params.get("r");
    if (r && info && !responded) { const id = setTimeout(() => respond(r), 0); return () => clearTimeout(id); }
    return undefined;
  }, [info, params, responded, respond]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500 label-mono">LOADING...</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="tac-card p-10 max-w-md text-center">
          <XCircle size={36} className="mx-auto text-red-500 mb-3" weight="duotone"/>
          <h1 className="font-display text-2xl font-bold">{error}</h1>
          <p className="text-zinc-500 text-sm mt-2">Please contact your dispatcher.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] bg-grid scanline" data-testid="checkin-page">
      <div className="max-w-lg mx-auto p-6 sm:p-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 grid place-items-center bg-[#007AFF]"><Shield size={20} weight="fill"/></div>
          <div>
            <div className="font-display font-black text-xl">SANDY</div>
            <div className="label-mono">Guard Check-In</div>
          </div>
        </div>

        <div className="tac-card p-6 mb-6">
          <div className="label-mono mb-2">// SHIFT BRIEFING</div>
          <h1 className="font-display text-2xl font-bold mb-4">Hi {info.guard_name},</h1>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <MapPinLine size={18} className="text-[#2BD4D9] mt-0.5" weight="duotone"/>
              <div>
                <div className="font-semibold">{info.site_name}</div>
                <div className="text-zinc-400 text-xs">{info.site_address}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <ClockClockwise size={18} className="text-[#2BD4D9] mt-0.5" weight="duotone"/>
              <div>
                <div className="font-semibold">Shift Start</div>
                <div className="text-zinc-400 text-xs font-mono">{format(new Date(info.start_time), "EEE, MMM d · HH:mm 'UTC'")}</div>
              </div>
            </div>
            {info.geofence_enabled && (
              <div className="flex items-start gap-3 pt-2 border-t border-white/5">
                <Crosshair size={18} className="text-[#2BD4D9] mt-0.5" weight="duotone"/>
                <div>
                  <div className="font-semibold text-xs uppercase tracking-wider">GPS verified arrival</div>
                  <div className="text-zinc-500 text-xs">Tap a status — your phone may ask permission for location.</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {responded ? (
          <div className="tac-card p-8 text-center" data-testid="checkin-confirmation">
            {verifyState?.verified === false ? (
              <>
                <WarningOctagon size={36} className="mx-auto text-yellow-400 mb-3" weight="duotone"/>
                <h2 className="font-display text-xl font-bold mb-1">Recorded — but outside fence</h2>
                <p className="text-zinc-400 text-sm">You&apos;re <span className="font-mono text-white">{Math.round(verifyState.distance_m)}m</span> from the site center.</p>
                <p className="text-zinc-500 text-xs mt-3">Dispatch has been notified to verify your location.</p>
              </>
            ) : verifyState?.verified === true ? (
              <>
                <CheckCircle size={36} className="mx-auto text-emerald-500 mb-3" weight="duotone"/>
                <h2 className="font-display text-xl font-bold mb-1">On post — GPS verified ✓</h2>
                <p className="text-zinc-400 text-sm">You&apos;re <span className="font-mono text-white">{Math.round(verifyState.distance_m)}m</span> from the site center.</p>
                <p className="text-zinc-500 text-xs mt-3">Status: <span className="font-mono uppercase text-white">{info.status}</span></p>
              </>
            ) : (
              <>
                <CheckCircle size={36} className="mx-auto text-emerald-500 mb-3" weight="duotone"/>
                <h2 className="font-display text-xl font-bold mb-1">Response recorded</h2>
                <p className="text-zinc-400 text-sm">Status: <span className="font-mono uppercase text-white">{info.status}</span></p>
                <p className="text-zinc-500 text-xs mt-3">Dispatch has been notified.</p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="label-mono mb-3">// CONFIRM YOUR STATUS</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Choice value="arrived" label="Arrived" color="border-emerald-500/40 text-emerald-400 hover:border-emerald-400" icon={CheckCircle} onClick={respond} disabled={busy} testid="respond-arrived"/>
              <Choice value="late" label="Running Late" color="border-yellow-500/40 text-yellow-400 hover:border-yellow-400" icon={WarningOctagon} onClick={respond} disabled={busy} testid="respond-late"/>
              <Choice value="cant_make_it" label="Can't Make It" color="border-red-500/40 text-red-400 hover:border-red-400" icon={XCircle} onClick={respond} disabled={busy} testid="respond-cant-make-it"/>
            </div>
            <p className="text-[0.65rem] text-zinc-600 mt-6 font-mono uppercase tracking-wider text-center">
              {busy ? "Locating your phone..." : "By responding you confirm your identity on the Sandy network."}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckIn;
